import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Terminal, Gift, Sparkles, Wallet } from 'lucide-react'

const HeroSection: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.4,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 1.2,
        ease: 'easeOut',
      },
    },
  }

  const iconVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 1.5,
        ease: 'easeOut',
      },
    },
  }

  return (
    <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-secondary-700 dark:via-secondary-800 dark:to-secondary-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(254,66,21,0.05),transparent_50%)] dark:bg-[radial-gradient(circle_at_30%_50%,rgba(254,66,21,0.1),transparent_50%)] pointer-events-none"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(35,61,113,0.05),transparent_50%)] dark:bg-[radial-gradient(circle_at_80%_20%,rgba(35,61,113,0.3),transparent_50%)] pointer-events-none"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="text-center"
        >
          {/* Logo Icon */}
          <motion.div
            variants={iconVariants}
            className="flex justify-center mb-12"
          >
            <div className="w-32 h-32 md:w-40 md:h-40 lg:w-48 lg:h-48 flex items-center justify-center">
              <img 
                src="/assets/images/logo-icon.png" 
                alt="MomentScience Logo" 
                className="w-full h-full object-contain drop-shadow-2xl"
              />
            </div>
          </motion.div>

          {/* Main Title */}
          <motion.h1
            variants={itemVariants}
            className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight"
          >
            <span className="bg-gradient-to-r from-primary-400 via-primary-500 to-primary-600 bg-clip-text text-transparent">
              MomentScience
            </span>
            <br />
            <span className="text-gray-700 dark:text-white font-light">Labs</span>
          </motion.h1>

          {/* Action Buttons - Auto-scrolling Slider */}
          <motion.div
            variants={itemVariants}
            className="mt-12 md:mt-16 lg:mt-20 overflow-hidden relative"
            style={{
              width: '100vw',
              marginLeft: 'calc(-50vw + 50%)'
            }}
          >
            <div className="flex gap-6 animate-infinite-scroll">
              {/* First set of buttons */}
              <Link
                to="/api-explorer"
                className="inline-flex items-center justify-center space-x-3 px-6 py-4 bg-[#FE4215] text-white rounded-lg hover:bg-[#E53910] transition-all duration-300 font-medium text-base xl:text-lg font-sans min-h-[64px] whitespace-nowrap flex-shrink-0"
                style={{ 
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0 4px 6px -1px rgba(254, 66, 21, 0.2), 0 2px 4px -1px rgba(254, 66, 21, 0.1)'
                }}
              >
                <Terminal className="w-5 h-5 xl:w-6 xl:h-6 flex-shrink-0" />
                <span>Explore APIs</span>
              </Link>
              
              <Link
                to="/usp-setup"
                className="inline-flex items-center justify-center space-x-3 px-6 py-4 bg-[#233D71] text-white rounded-lg hover:bg-[#1A2B49] transition-all duration-300 font-medium text-base xl:text-lg font-sans min-h-[64px] whitespace-nowrap flex-shrink-0"
                style={{ 
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0 4px 6px -1px rgba(35, 61, 113, 0.2), 0 2px 4px -1px rgba(35, 61, 113, 0.1)'
                }}
              >
                <Gift className="w-5 h-5 xl:w-6 xl:h-6 flex-shrink-0" />
                <span>User Selected Perks</span>
              </Link>

              <Link
                to="/moment-perks"
                className="inline-flex items-center justify-center space-x-3 px-6 py-4 bg-[#3B82F6] text-white rounded-lg hover:bg-[#2563EB] transition-all duration-300 font-medium text-base xl:text-lg font-sans min-h-[64px] whitespace-nowrap flex-shrink-0"
                style={{ 
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.2), 0 2px 4px -1px rgba(59, 130, 246, 0.1)'
                }}
              >
                <Sparkles className="w-5 h-5 xl:w-6 xl:h-6 flex-shrink-0" />
                <span>MomentPerks</span>
              </Link>
              
              <Link
                to="/perkswall"
                className="inline-flex items-center justify-center space-x-3 px-6 py-4 bg-[#8B5CF6] text-white rounded-lg hover:bg-[#7C3AED] transition-all duration-300 font-medium text-base xl:text-lg font-sans min-h-[64px] whitespace-nowrap flex-shrink-0"
                style={{ 
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0 4px 6px -1px rgba(139, 92, 246, 0.2), 0 2px 4px -1px rgba(139, 92, 246, 0.1)'
                }}
              >
                <Wallet className="w-5 h-5 xl:w-6 xl:h-6 flex-shrink-0" />
                <span>Perkswall</span>
              </Link>

              <Link
                to="/shopify"
                className="inline-flex items-center justify-center space-x-3 px-6 py-4 bg-[#95BF47] text-white rounded-lg hover:bg-[#7DA53D] transition-all duration-300 font-medium text-base xl:text-lg font-sans min-h-[64px] whitespace-nowrap flex-shrink-0"
                style={{ 
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0 4px 6px -1px rgba(149, 191, 71, 0.2), 0 2px 4px -1px rgba(149, 191, 71, 0.1)'
                }}
              >
                <img src="https://cdn3.iconfinder.com/data/icons/social-media-2068/64/_shopping-512.png" alt="Shopify" className="w-5 h-5 xl:w-6 xl:h-6 flex-shrink-0" />
                <span>Shopify</span>
              </Link>

              {/* Second set for seamless loop */}
              <Link
                to="/api-explorer"
                className="inline-flex items-center justify-center space-x-3 px-6 py-4 bg-[#FE4215] text-white rounded-lg hover:bg-[#E53910] transition-all duration-300 font-medium text-base xl:text-lg font-sans min-h-[64px] whitespace-nowrap flex-shrink-0"
                style={{ 
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0 4px 6px -1px rgba(254, 66, 21, 0.2), 0 2px 4px -1px rgba(254, 66, 21, 0.1)'
                }}
              >
                <Terminal className="w-5 h-5 xl:w-6 xl:h-6 flex-shrink-0" />
                <span>Explore APIs</span>
              </Link>
              
              <Link
                to="/usp-setup"
                className="inline-flex items-center justify-center space-x-3 px-6 py-4 bg-[#233D71] text-white rounded-lg hover:bg-[#1A2B49] transition-all duration-300 font-medium text-base xl:text-lg font-sans min-h-[64px] whitespace-nowrap flex-shrink-0"
                style={{ 
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0 4px 6px -1px rgba(35, 61, 113, 0.2), 0 2px 4px -1px rgba(35, 61, 113, 0.1)'
                }}
              >
                <Gift className="w-5 h-5 xl:w-6 xl:h-6 flex-shrink-0" />
                <span>User Selected Perks</span>
              </Link>

              <Link
                to="/moment-perks"
                className="inline-flex items-center justify-center space-x-3 px-6 py-4 bg-[#3B82F6] text-white rounded-lg hover:bg-[#2563EB] transition-all duration-300 font-medium text-base xl:text-lg font-sans min-h-[64px] whitespace-nowrap flex-shrink-0"
                style={{ 
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.2), 0 2px 4px -1px rgba(59, 130, 246, 0.1)'
                }}
              >
                <Sparkles className="w-5 h-5 xl:w-6 xl:h-6 flex-shrink-0" />
                <span>MomentPerks</span>
              </Link>
              
              <Link
                to="/perkswall"
                className="inline-flex items-center justify-center space-x-3 px-6 py-4 bg-[#8B5CF6] text-white rounded-lg hover:bg-[#7C3AED] transition-all duration-300 font-medium text-base xl:text-lg font-sans min-h-[64px] whitespace-nowrap flex-shrink-0"
                style={{ 
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0 4px 6px -1px rgba(139, 92, 246, 0.2), 0 2px 4px -1px rgba(139, 92, 246, 0.1)'
                }}
              >
                <Wallet className="w-5 h-5 xl:w-6 xl:h-6 flex-shrink-0" />
                <span>Perkswall</span>
              </Link>

              <Link
                to="/shopify"
                className="inline-flex items-center justify-center space-x-3 px-6 py-4 bg-[#95BF47] text-white rounded-lg hover:bg-[#7DA53D] transition-all duration-300 font-medium text-base xl:text-lg font-sans min-h-[64px] whitespace-nowrap flex-shrink-0"
                style={{ 
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0 4px 6px -1px rgba(149, 191, 71, 0.2), 0 2px 4px -1px rgba(149, 191, 71, 0.1)'
                }}
              >
                <img src="https://cdn3.iconfinder.com/data/icons/social-media-2068/64/_shopping-512.png" alt="Shopify" className="w-5 h-5 xl:w-6 xl:h-6 flex-shrink-0" />
                <span>Shopify</span>
              </Link>

              {/* Third set for seamless loop */}
              <Link
                to="/api-explorer"
                className="inline-flex items-center justify-center space-x-3 px-6 py-4 bg-[#FE4215] text-white rounded-lg hover:bg-[#E53910] transition-all duration-300 font-medium text-base xl:text-lg font-sans min-h-[64px] whitespace-nowrap flex-shrink-0"
                style={{ 
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0 4px 6px -1px rgba(254, 66, 21, 0.2), 0 2px 4px -1px rgba(254, 66, 21, 0.1)'
                }}
              >
                <Terminal className="w-5 h-5 xl:w-6 xl:h-6 flex-shrink-0" />
                <span>Explore APIs</span>
              </Link>
              
              <Link
                to="/usp-setup"
                className="inline-flex items-center justify-center space-x-3 px-6 py-4 bg-[#233D71] text-white rounded-lg hover:bg-[#1A2B49] transition-all duration-300 font-medium text-base xl:text-lg font-sans min-h-[64px] whitespace-nowrap flex-shrink-0"
                style={{ 
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0 4px 6px -1px rgba(35, 61, 113, 0.2), 0 2px 4px -1px rgba(35, 61, 113, 0.1)'
                }}
              >
                <Gift className="w-5 h-5 xl:w-6 xl:h-6 flex-shrink-0" />
                <span>User Selected Perks</span>
              </Link>

              <Link
                to="/moment-perks"
                className="inline-flex items-center justify-center space-x-3 px-6 py-4 bg-[#3B82F6] text-white rounded-lg hover:bg-[#2563EB] transition-all duration-300 font-medium text-base xl:text-lg font-sans min-h-[64px] whitespace-nowrap flex-shrink-0"
                style={{ 
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.2), 0 2px 4px -1px rgba(59, 130, 246, 0.1)'
                }}
              >
                <Sparkles className="w-5 h-5 xl:w-6 xl:h-6 flex-shrink-0" />
                <span>MomentPerks</span>
              </Link>
              
              <Link
                to="/perkswall"
                className="inline-flex items-center justify-center space-x-3 px-6 py-4 bg-[#8B5CF6] text-white rounded-lg hover:bg-[#7C3AED] transition-all duration-300 font-medium text-base xl:text-lg font-sans min-h-[64px] whitespace-nowrap flex-shrink-0"
                style={{ 
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0 4px 6px -1px rgba(139, 92, 246, 0.2), 0 2px 4px -1px rgba(139, 92, 246, 0.1)'
                }}
              >
                <Wallet className="w-5 h-5 xl:w-6 xl:h-6 flex-shrink-0" />
                <span>Perkswall</span>
              </Link>

              <Link
                to="/shopify"
                className="inline-flex items-center justify-center space-x-3 px-6 py-4 bg-[#95BF47] text-white rounded-lg hover:bg-[#7DA53D] transition-all duration-300 font-medium text-base xl:text-lg font-sans min-h-[64px] whitespace-nowrap flex-shrink-0"
                style={{ 
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0 4px 6px -1px rgba(149, 191, 71, 0.2), 0 2px 4px -1px rgba(149, 191, 71, 0.1)'
                }}
              >
                <img src="https://cdn3.iconfinder.com/data/icons/social-media-2068/64/_shopping-512.png" alt="Shopify" className="w-5 h-5 xl:w-6 xl:h-6 flex-shrink-0" />
                <span>Shopify</span>
              </Link>

              {/* Fourth set for even smoother loop */}
              <Link
                to="/api-explorer"
                className="inline-flex items-center justify-center space-x-3 px-6 py-4 bg-[#FE4215] text-white rounded-lg hover:bg-[#E53910] transition-all duration-300 font-medium text-base xl:text-lg font-sans min-h-[64px] whitespace-nowrap flex-shrink-0"
                style={{ 
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0 4px 6px -1px rgba(254, 66, 21, 0.2), 0 2px 4px -1px rgba(254, 66, 21, 0.1)'
                }}
              >
                <Terminal className="w-5 h-5 xl:w-6 xl:h-6 flex-shrink-0" />
                <span>Explore APIs</span>
              </Link>
              
              <Link
                to="/usp-setup"
                className="inline-flex items-center justify-center space-x-3 px-6 py-4 bg-[#233D71] text-white rounded-lg hover:bg-[#1A2B49] transition-all duration-300 font-medium text-base xl:text-lg font-sans min-h-[64px] whitespace-nowrap flex-shrink-0"
                style={{ 
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0 4px 6px -1px rgba(35, 61, 113, 0.2), 0 2px 4px -1px rgba(35, 61, 113, 0.1)'
                }}
              >
                <Gift className="w-5 h-5 xl:w-6 xl:h-6 flex-shrink-0" />
                <span>User Selected Perks</span>
              </Link>

              <Link
                to="/moment-perks"
                className="inline-flex items-center justify-center space-x-3 px-6 py-4 bg-[#3B82F6] text-white rounded-lg hover:bg-[#2563EB] transition-all duration-300 font-medium text-base xl:text-lg font-sans min-h-[64px] whitespace-nowrap flex-shrink-0"
                style={{ 
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.2), 0 2px 4px -1px rgba(59, 130, 246, 0.1)'
                }}
              >
                <Sparkles className="w-5 h-5 xl:w-6 xl:h-6 flex-shrink-0" />
                <span>MomentPerks</span>
              </Link>
              
              <Link
                to="/perkswall"
                className="inline-flex items-center justify-center space-x-3 px-6 py-4 bg-[#8B5CF6] text-white rounded-lg hover:bg-[#7C3AED] transition-all duration-300 font-medium text-base xl:text-lg font-sans min-h-[64px] whitespace-nowrap flex-shrink-0"
                style={{ 
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0 4px 6px -1px rgba(139, 92, 246, 0.2), 0 2px 4px -1px rgba(139, 92, 246, 0.1)'
                }}
              >
                <Wallet className="w-5 h-5 xl:w-6 xl:h-6 flex-shrink-0" />
                <span>Perkswall</span>
              </Link>

              <Link
                to="/shopify"
                className="inline-flex items-center justify-center space-x-3 px-6 py-4 bg-[#95BF47] text-white rounded-lg hover:bg-[#7DA53D] transition-all duration-300 font-medium text-base xl:text-lg font-sans min-h-[64px] whitespace-nowrap flex-shrink-0"
                style={{ 
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0 4px 6px -1px rgba(149, 191, 71, 0.2), 0 2px 4px -1px rgba(149, 191, 71, 0.1)'
                }}
              >
                <img src="https://cdn3.iconfinder.com/data/icons/social-media-2068/64/_shopping-512.png" alt="Shopify" className="w-5 h-5 xl:w-6 xl:h-6 flex-shrink-0" />
                <span>Shopify</span>
              </Link>
            </div>

            <style>{`
              @keyframes infinite-scroll {
                0% {
                  transform: translateX(0);
                }
                100% {
                  transform: translateX(calc(-100% / 4));
                }
              }
              
              .animate-infinite-scroll {
                animation: infinite-scroll 40s linear infinite;
                will-change: transform;
              }
              
              .animate-infinite-scroll:hover {
                animation-play-state: paused;
              }
            `}</style>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

export default HeroSection
